import type { Action, ActionLane } from "../types";

export function nextActionSortOrder(actions: Pick<Action, "sort_order">[]): number {
  return Math.min(0, ...actions.map((action) => action.sort_order)) - 1;
}

export function actionsForLaneGroup(actions: Action[], lane: ActionLane): Action[] {
  const byId = new Map(actions.map((action) => [action.id, action]));
  return actions.filter((action) => {
    if (!action.parent_id) return action.lane === lane;
    const parent = byId.get(action.parent_id);
    return parent ? parent.lane === lane : action.lane === lane;
  });
}

export function effectiveActionLane(action: Action, allActions: Action[]): ActionLane {
  if (!action.parent_id) return action.lane;
  return allActions.find((item) => item.id === action.parent_id)?.lane || action.lane;
}

export function actionTicketBlockKey(action: Pick<Action, "ticket_key" | "ticket_id">): string | null {
  if (action.ticket_key) return `catalog:${action.ticket_key}`;
  if (action.ticket_id) return `ticket:${action.ticket_id}`;
  return null;
}

function compareActions(a: Action, b: Action): number {
  return a.sort_order - b.sort_order || a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id);
}

export function actionTicketBlockRoots(actions: Action[], action: Action): Action[] {
  const root = action.parent_id ? actions.find((item) => item.id === action.parent_id) || action : action;
  const ticketKey = actionTicketBlockKey(root);
  if (!ticketKey || root.parent_id) return [root];
  return actions
    .filter((item) => !item.parent_id && item.lane === root.lane && actionTicketBlockKey(item) === ticketKey)
    .sort(compareActions);
}

export function orderActionRootsInTicketBlocks(actions: Action[]): Action[] {
  const roots = actions.filter((action) => !action.parent_id).sort(compareActions);
  const groups = new Map<string, Action[]>();
  for (const root of roots) {
    const ticketKey = actionTicketBlockKey(root);
    if (!ticketKey) continue;
    const groupKey = `${root.lane}:${ticketKey}`;
    groups.set(groupKey, [...(groups.get(groupKey) || []), root]);
  }

  const included = new Set<string>();
  const ordered: Action[] = [];
  for (const root of roots) {
    if (included.has(root.id)) continue;
    const ticketKey = actionTicketBlockKey(root);
    const block = ticketKey ? groups.get(`${root.lane}:${ticketKey}`) || [root] : [root];
    for (const item of block) {
      ordered.push(item);
      included.add(item.id);
    }
  }
  return ordered;
}

export function orderActionsWithChildren(actions: Action[]): Action[] {
  const sorted = [...actions].sort(compareActions);
  const roots = orderActionRootsInTicketBlocks(sorted);
  const included = new Set<string>();
  const ordered: Action[] = [];

  for (const root of roots) {
    ordered.push(root);
    included.add(root.id);
    for (const child of sorted.filter((action) => action.parent_id === root.id)) {
      ordered.push(child);
      included.add(child.id);
    }
  }

  return [...ordered, ...sorted.filter((action) => !included.has(action.id))];
}
